import React, {
  useEffect, useImperativeHandle, useRef, forwardRef
} from 'react';
import WheelOfFortuneRenderer from 'rf-ui/GameRenderer/WheelOfFortuneRenderer';
import type { WheelPlayer } from 'rf-ui/GameRenderer/WheelOfFortuneRenderer';
import { WheelEvent } from '@lykkehjulet/db-model';
import { getColorFromName } from 'rf-lib';

type RemainingPlayer = {
  playerId: string;
  tickets: number;
};

type PlayerProfile = {
  uid: string;
  displayName?: string;
  color?: string;
};

type GameRendererProps = {
  gameId: string;
  game: any;
  winners: string[] | null;
  isBehind: boolean;
  numberOfPrizes: number;
  allPlayers: PlayerProfile[];
  remainingPlayers: RemainingPlayer[];
  onAnimationFinished: (roundNumber: number) => void;
};

type GameRendererRef = {
  animateRound: (event: WheelEvent, roundNumber: number) => void;
};

const mapAllPlayers = (allPlayers: PlayerProfile[]): WheelPlayer[] => (
  allPlayers.map(player => {
    const playerName = player?.displayName || '<Unknown>';
    return {
      playerId: player.uid,
      playerName,
      color: player?.color || getColorFromName(playerName),
    };
  })
);

const sortPlayersBySeed = (players: WheelPlayer[], seed: string): WheelPlayer[] => {
  function fnv1a32(str: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h >>> 0;
  }

  return [...players].sort((a, b) => {
    const ha = fnv1a32(`${seed}:${a.playerId}`);
    const hb = fnv1a32(`${seed}:${b.playerId}`);

    if (ha !== hb) return ha - hb;

    return a.playerId.localeCompare(b.playerId);
  });
};

const GameRenderer = forwardRef<GameRendererRef, GameRendererProps>(({
  gameId,
  game,
  winners,
  isBehind,
  numberOfPrizes,
  allPlayers,
  remainingPlayers,
  onAnimationFinished
}, ref) => {
  const allPlayersSorted = sortPlayersBySeed(mapAllPlayers(allPlayers), gameId);
  const domElement = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<WheelOfFortuneRenderer | null>(null);

  useEffect(() => {
    const renderer = new WheelOfFortuneRenderer(
      gameId,
      game,
      domElement,
      allPlayersSorted,
      remainingPlayers,
      numberOfPrizes,
      onAnimationFinished
    );
    rendererRef.current = renderer;
    return () => {
      renderer.unmount();
      rendererRef.current = null;
    };
  }, []);

  useEffect(() => {
    rendererRef.current?.updateCurrentPlayers(allPlayersSorted, remainingPlayers, winners);
  }, [allPlayersSorted, remainingPlayers, winners]);

  const animateRound = (event: WheelEvent, roundNumber: number) => {
    rendererRef.current?.animateRound(event, roundNumber, isBehind);
  };

  useImperativeHandle(ref, () => ({
    animateRound
  }));

  return <div ref={domElement} />;
});

export default GameRenderer;
